import { useState } from "react";

const SUBMIT_ADDR = "https://readdy.ai/api/form/db6c0unqhq9pk5mecsa0";

export default function ContactForm() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">(
    "idle"
  );
  const [errorMsg, setErrorMsg] = useState("");

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setErrorMsg("");

    const form = event.currentTarget;
    const formData = new FormData(form);

    // 反垃圾：蜜罐字段被填写则静默按成功处理，不发送请求
    const honeypot = String(formData.get("company_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      form.reset();
      return;
    }

    // 前端基础校验
    const name = String(formData.get("name") ?? "").trim();
    const email = String(formData.get("email") ?? "").trim();
    const message = String(formData.get("message") ?? "").trim();
    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

    if (!name || !email || !message) {
      setStatus("error");
      setErrorMsg("请完整填写姓名、邮箱与留言内容后再提交。");
      return;
    }
    if (!emailOk) {
      setStatus("error");
      setErrorMsg("请输入有效的邮箱地址，例如 name@example.com。");
      return;
    }
    if (message.length > 500) {
      setStatus("error");
      setErrorMsg("留言内容不能超过 500 个字符，请精简后再提交。");
      return;
    }

    // 由真实表单元素构建提交内容，排除蜜罐与空值
    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "company_alt") return;
      const str = String(value);
      if (str.trim() === "") return;
      payload.append(key, str);
    });

    setStatus("submitting");
    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: Record<string, unknown> | null = null;
      try {
        parsed = JSON.parse(responseText) as Record<string, unknown>;
      } catch {
        parsed = null;
      }

      const meta = (parsed?.meta ?? {}) as Record<string, unknown>;
      const serverMsg = String(
        meta.message ?? parsed?.message ?? meta.detail ?? responseText ?? ""
      );
      const code = parsed?.code;
      const isSpam = serverMsg.toLowerCase().includes("spam");

      if (response.ok && code === "OK" && !isSpam) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setErrorMsg(
          serverMsg || "提交失败，请稍后重试，或通过邮箱与我们直接联系。"
        );
      }
    } catch {
      setStatus("error");
      setErrorMsg("网络异常，提交失败，请检查网络后重试。");
    }
  };

  return (
    <form
      id="contact-message-form"
      data-readdy-form
      onSubmit={handleSubmit}
      className="rounded-xl bg-background-50 border border-background-200 p-5 md:p-6"
    >
      <h2 className="font-heading text-xl font-black text-foreground-950 mb-1.5">
        在线留言
      </h2>
      <p className="text-sm text-foreground-500 mb-5">
        有建议、合作或内容反馈？填写下面的表单告诉我们。
      </p>

      <div className="space-y-4">
        <div>
          <label htmlFor="contact-name" className="block text-sm text-foreground-700 mb-1.5">
            姓名 <span className="text-primary-500">*</span>
          </label>
          <input
            id="contact-name"
            name="name"
            type="text"
            required
            placeholder="请输入您的姓名"
            className="w-full px-4 py-2.5 text-sm rounded-lg bg-background-100 border border-background-200 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:bg-background-50 transition-colors"
          />
        </div>

        <div>
          <label htmlFor="contact-email" className="block text-sm text-foreground-700 mb-1.5">
            邮箱 <span className="text-primary-500">*</span>
          </label>
          <input
            id="contact-email"
            name="email"
            type="email"
            required
            placeholder="name@example.com"
            className="w-full px-4 py-2.5 text-sm rounded-lg bg-background-100 border border-background-200 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:bg-background-50 transition-colors"
          />
        </div>

        <div>
          <label htmlFor="contact-phone" className="block text-sm text-foreground-700 mb-1.5">
            联系电话 <span className="text-foreground-400">（选填）</span>
          </label>
          <input
            id="contact-phone"
            name="phone"
            type="text"
            placeholder="便于我们与您联系"
            className="w-full px-4 py-2.5 text-sm rounded-lg bg-background-100 border border-background-200 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:bg-background-50 transition-colors"
          />
        </div>

        <div>
          <label htmlFor="contact-message" className="block text-sm text-foreground-700 mb-1.5">
            留言内容 <span className="text-primary-500">*</span>
          </label>
          <textarea
            id="contact-message"
            name="message"
            required
            maxLength={500}
            rows={5}
            placeholder="请填写您的留言内容（不超过 500 字）"
            className="w-full px-4 py-2.5 text-sm rounded-lg bg-background-100 border border-background-200 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:bg-background-50 transition-colors resize-none"
          />
        </div>

        {/* 反垃圾蜜罐字段：无内联隐藏、无可疑命名 */}
        <input
          className="field-backup-verify"
          type="text"
          name="company_alt"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          readOnly
        />
      </div>

      {status === "success" && (
        <div className="mt-5 flex items-start gap-2 rounded-lg bg-accent-50 border border-accent-200 px-4 py-3">
          <i className="ri-checkbox-circle-line text-accent-600 text-lg mt-0.5" />
          <p className="text-sm text-accent-900">
            留言已提交成功，感谢您的反馈，我们会尽快查看并回复。
          </p>
        </div>
      )}

      {status === "error" && (
        <div className="mt-5 flex items-start gap-2 rounded-lg bg-primary-50 border border-primary-200 px-4 py-3">
          <i className="ri-error-warning-line text-primary-600 text-lg mt-0.5" />
          <p className="text-sm text-primary-900">{errorMsg}</p>
        </div>
      )}

      <button
        type="submit"
        disabled={status === "submitting"}
        className="mt-5 w-full sm:w-auto px-8 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {status === "submitting" ? "提交中…" : "提交留言"}
      </button>
    </form>
  );
}